import * as React from "react";
import { Button } from "antd";
import { DateInput } from "./components/DateInput";
import { CalendarBox } from "./components/CalendarBox";
import { PresetList } from "./components/PresetList";
import { TimezoneSelector } from "./components/TimezoneSelector";
import { Popover } from "./components/Popover";
import { PickerTooltip } from "./components/PickerTooltip";
import {
  parse,
  formatDateToYMD,
  isAbsoluteDate,
  getSystemTimezone,
  resolveTimezone,
  formatTooltipWithTimezone,
  extractTimeFromDate,
  combineDateAndTime,
  formatDateWithGranularity,
  validateDateConstraints,
  DATE_ERRORS,
  parseTimeInput,
  isExpression as checkIsExpression,
  applyExpression,
  resolveExpression,
  convertValueForTimezoneSwitch,
} from "./utils/parser";
import {
  today,
  dateToCalendarDateTime,
  calendarDateTimeToDate,
  truncateToGranularity,
  formatCalendarDateTime,
} from "./utils/calendarDateTime";
import type { DatePickerProps, SinglePreset, TimeGranularity } from "./types";
import "./DatePicker.css";

// =============================================================================
// DatePicker Component
// Single date/time selection with calendar, presets, and timezone support
// When granularity is "date", behaves as a date-only picker (time set to 00:00:00)
// =============================================================================

const LOCAL_TIMEZONE = getSystemTimezone();

export const DatePicker = React.memo(function DatePicker({
  value,
  onChange,
  timezoneValue,
  timezoneValueType,
  onTimezoneChange,
  timezoneSwitchBehavior = "keepDisplay",
  presets,
  granularity = "date",
  minDate,
  maxDate,
  required = false,
  allowClear = false,
  disabled = false,
  placeholder,
  immediatelyResolveExpressions = false,
  className,
  style,
}: DatePickerProps) {
  // Resolve effective timezone - timezoneValueType overrides timezoneValue, default to UTC
  const effectiveTimezone = React.useMemo(
    () => resolveTimezone(timezoneValue, timezoneValueType),
    [timezoneValueType, timezoneValue]
  );

  // Whether to show time input (date-only mode hides time)
  const showTimeInput = granularity !== "date";

  // Effective placeholder based on granularity and required
  const basePlaceholder = placeholder ?? (showTimeInput ? "Select date and time" : "Select date");
  const effectivePlaceholder = required ? `${basePlaceholder} (Required)` : basePlaceholder;

  // Required validation error
  const requiredError = required && !value;

  // ---------------------------------------------------------------------------
  // Resolve Min/Max Dates
  // ---------------------------------------------------------------------------

  // Parse minDate/maxDate using the parser - accepts any valid expression
  const resolvedMinDate = React.useMemo(() => {
    if (!minDate) return undefined;
    const parsed = parse(minDate, { pickerType: "single", timezone: "UTC" });
    if (parsed.isValid && parsed.resolved) {
      return formatDateToYMD(parsed.resolved);
    }
    console.error(`[DatePicker] Invalid minDate: "${minDate}"`);
    return undefined;
  }, [minDate]);

  const resolvedMaxDate = React.useMemo(() => {
    if (!maxDate) return undefined;
    const parsed = parse(maxDate, { pickerType: "single", timezone: "UTC" });
    if (parsed.isValid && parsed.resolved) {
      return formatDateToYMD(parsed.resolved);
    }
    console.error(`[DatePicker] Invalid maxDate: "${maxDate}"`);
    return undefined;
  }, [maxDate]);

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [isOpen, setIsOpen] = React.useState(false);
  const [inputText, setInputText] = React.useState("");
  const [errorReason, setErrorReason] = React.useState<string | null>(null);
  // undefined = no baseline captured, null = baseline was empty, string = baseline was a date
  const [previousValue, setPreviousValue] = React.useState<string | null | undefined>(undefined);
  // Initialize calendar to today using library
  const todayDate = today("UTC");
  const [calendarYear, setCalendarYear] = React.useState(() => todayDate.year);
  const [calendarMonth, setCalendarMonth] = React.useState(() => todayDate.month);
  // Hover tick to force re-render for dynamic expression tooltip evaluation
  const [, setHoverTick] = React.useState(0);
  const [timeValue, setTimeValue] = React.useState("");
  // Time input error state
  const [timeError, setTimeError] = React.useState(false);
  // Previous valid time value for undo
  const [previousTimeValue, setPreviousTimeValue] = React.useState<string | null>(null);

  const anchorRef = React.useRef<HTMLDivElement>(null);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  // Format date for display based on granularity
  const formatForDisplay = React.useCallback(
    (date: Date): string => formatDateWithGranularity(date, granularity),
    [granularity]
  );

  // Extract time string from Date based on granularity (for time input)
  const extractTime = React.useCallback(
    (date: Date): string => {
      if (granularity === "date") return "";
      return extractTimeFromDate(date, granularity as "hour" | "minute" | "second");
    },
    [granularity]
  );

  // Build ISO string from date and time
  const buildISOString = React.useCallback(
    (dateStr: string, timeStr: string): string => {
      if (granularity === "date") {
        // Date-only mode: always midnight
        return `${dateStr}T00:00:00Z`;
      }
      return combineDateAndTime(dateStr, timeStr);
    },
    [granularity]
  );

  // ---------------------------------------------------------------------------
  // Sync State with Props
  // ---------------------------------------------------------------------------

  // Normalize value based on granularity using CalendarDateTime
  // - date: zero out hours, minutes, seconds, milliseconds
  // - hour: zero out minutes, seconds, milliseconds
  // - minute: zero out seconds, milliseconds
  // - second: zero out milliseconds
  React.useEffect(() => {
    if (!value || !isAbsoluteDate(value)) return;

    const date = new Date(value);
    // Guard against invalid dates (e.g., from corrupted URL params)
    if (Number.isNaN(date.getTime())) {
      onChange(null);
      return;
    }

    // Convert to CalendarDateTime and truncate based on granularity
    const cdt = dateToCalendarDateTime(date);
    const truncated = truncateToGranularity(cdt, granularity);

    // Check if truncation changed anything
    const needsNormalization =
      cdt.hour !== truncated.hour ||
      cdt.minute !== truncated.minute ||
      cdt.second !== truncated.second ||
      cdt.millisecond !== truncated.millisecond;

    if (needsNormalization) {
      const normalizedDate = calendarDateTimeToDate(truncated);
      onChange(normalizedDate.toISOString());
    }
  }, [value, granularity, onChange]);

  // Update input text and time when value changes
  React.useEffect(() => {
    if (!value) {
      setInputText("");
      setTimeValue("");
      setErrorReason(null);
    } else if (isAbsoluteDate(value)) {
      // Absolute date - format for display
      const date = new Date(value);
      // Skip processing if date is invalid (will be cleared by normalization effect)
      if (Number.isNaN(date.getTime())) return;
      setInputText(formatForDisplay(date));
      setTimeValue(extractTime(date));
      setErrorReason(null);
      setCalendarYear(date.getUTCFullYear());
      setCalendarMonth(date.getUTCMonth() + 1);
    } else {
      // Expression - show as-is and resolve for calendar positioning
      setInputText(value);
      setErrorReason(null);
      const parsed = parse(value, { pickerType: "single", timezone: effectiveTimezone });
      if (parsed.resolved) {
        setTimeValue(extractTime(parsed.resolved));
        setCalendarYear(parsed.resolved.getUTCFullYear());
        setCalendarMonth(parsed.resolved.getUTCMonth() + 1);
      }
    }
  }, [value, effectiveTimezone, formatForDisplay, extractTime]);

  // ---------------------------------------------------------------------------
  // Event Handlers
  // ---------------------------------------------------------------------------

  const handleInputClick = React.useCallback(() => {
    if (disabled) return;
    // Capture baseline value when opening the picker
    if (!isOpen) {
      setPreviousValue(value);
      // Reset calendar to selected date's month when opening
      if (value) {
        if (isAbsoluteDate(value)) {
          const date = new Date(value);
          setCalendarYear(date.getUTCFullYear());
          setCalendarMonth(date.getUTCMonth() + 1);
        } else {
          const parsed = parse(value, { pickerType: "single", timezone: effectiveTimezone });
          if (parsed.resolved) {
            setCalendarYear(parsed.resolved.getUTCFullYear());
            setCalendarMonth(parsed.resolved.getUTCMonth() + 1);
          }
        }
      }
    }
    setIsOpen(true);
  }, [disabled, isOpen, value, effectiveTimezone]);

  const handleInputFocus = React.useCallback(() => {
    if (disabled) return;
    // Don't open on focus, only on click
  }, [disabled]);

  const handleInputChange = React.useCallback(
    (newText: string) => {
      setInputText(newText);

      // Try to parse the input using the parser
      const parsed = parse(newText, { pickerType: "single", timezone: effectiveTimezone });

      if (parsed.isValid && parsed.resolved) {
        // Check min/max date constraints using parser utility
        const resolvedDateStr = formatDateToYMD(parsed.resolved);
        const constraintResult = validateDateConstraints(resolvedDateStr, resolvedMinDate, resolvedMaxDate);

        if (!constraintResult.isValid) {
          setErrorReason(constraintResult.error!);
          return;
        }

        setErrorReason(null);

        // KEY: Preserve expressions unless immediatelyResolveExpressions is true
        if (parsed.isExpression && !immediatelyResolveExpressions) {
          // Grafana expression - pass normalized form (e.g., "now-7d")
          onChange(parsed.normalized);
        } else {
          // Natural language, absolute date, or expression with immediate resolve - resolve to ISO
          onChange(parsed.resolved.toISOString());
        }

        // When immediatelyResolveExpressions, update input to show resolved date
        if (parsed.isExpression && immediatelyResolveExpressions) {
          setInputText(formatForDisplay(parsed.resolved));
        }

        // Update time value and calendar
        setTimeValue(extractTime(parsed.resolved));
        setCalendarYear(parsed.resolved.getUTCFullYear());
        setCalendarMonth(parsed.resolved.getUTCMonth() + 1);
      } else if (newText === "") {
        setErrorReason(null);
        onChange(null);
      } else {
        setErrorReason(parsed.error || DATE_ERRORS.INVALID_FORMAT);
      }
    },
    [effectiveTimezone, onChange, resolvedMinDate, resolvedMaxDate, immediatelyResolveExpressions, formatForDisplay]
  );

  const handleClear = React.useCallback(() => {
    setInputText("");
    setTimeValue("");
    setErrorReason(null);
    onChange(null);
  }, [onChange]);

  const handleUndo = React.useCallback(() => {
    // previousValue can be undefined (no baseline), null (empty baseline), or string (date/expression baseline)
    if (previousValue !== undefined) {
      if (previousValue === null) {
        setInputText("");
        setTimeValue("");
        onChange(null);
      } else if (isAbsoluteDate(previousValue)) {
        // Absolute date - format for display
        const date = new Date(previousValue);
        setInputText(formatForDisplay(date));
        setTimeValue(extractTime(date));
        onChange(previousValue);
      } else {
        // Expression - show as-is
        setInputText(previousValue);
        const parsed = parse(previousValue, { pickerType: "single", timezone: effectiveTimezone });
        if (parsed.resolved) {
          setTimeValue(extractTime(parsed.resolved));
        }
        onChange(previousValue);
      }
      setErrorReason(null);
    }
  }, [previousValue, onChange, effectiveTimezone, formatForDisplay, extractTime]);

  const handleClose = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleDateClick = React.useCallback(
    (dateStr: string) => {
      // Validate time value before using - fall back to default if invalid
      let effectiveTime = "";
      if (granularity !== "date" && timeValue) {
        const timeGranularity = granularity as "hour" | "minute" | "second";
        const parsed = parseTimeInput(timeValue, timeGranularity);
        effectiveTime = parsed.isValid ? parsed.value : "";
      }

      // dateStr is YYYY-MM-DD, combine with validated time
      const isoDate = buildISOString(dateStr, effectiveTime);
      setErrorReason(null);
      onChange(isoDate);
      // In date-only mode, close immediately; otherwise user might want to adjust time
      if (granularity === "date") {
        setIsOpen(false);
      }
    },
    [onChange, timeValue, buildISOString, granularity]
  );

  // Store raw value on every keystroke (no parsing)
  const handleTimeChange = React.useCallback(
    (newTime: string) => {
      setTimeValue(newTime);
    },
    []
  );

  // Parse, normalize, and update on blur
  // CalendarBox now handles parsing and passes (time, isValid)
  const handleTimeBlur = React.useCallback(
    (newTime: string, isValid: boolean) => {
      // Skip time handling in date-only mode
      if (granularity === "date") return;

      // Update error state
      setTimeError(!isValid);

      // Save previous valid value for undo (before updating)
      if (isValid && newTime) {
        setPreviousTimeValue(timeValue);
      }

      // Only update value if valid
      if (!isValid) return;

      // Update local time value with the normalized time from CalendarBox
      setTimeValue(newTime);

      // If we have a current date, update the ISO string with the normalized time
      if (value && isAbsoluteDate(value)) {
        const date = new Date(value);
        const dateStr = formatDateToYMD(date);
        const newIso = buildISOString(dateStr, newTime);
        onChange(newIso);
      }
    },
    [value, onChange, granularity, buildISOString, timeValue]
  );

  // Time undo handler
  const handleTimeUndo = React.useCallback(() => {
    if (previousTimeValue !== null) {
      setTimeValue(previousTimeValue);
      setTimeError(false);
      if (value && isAbsoluteDate(value)) {
        const date = new Date(value);
        const dateStr = formatDateToYMD(date);
        const newIso = buildISOString(dateStr, previousTimeValue);
        onChange(newIso);
      }
    }
  }, [previousTimeValue, value, onChange, buildISOString]);

  const handleMonthChange = React.useCallback((year: number, month: number) => {
    setCalendarYear(year);
    setCalendarMonth(month);
  }, []);

  const handlePresetClick = React.useCallback(
    (preset: SinglePreset) => {
      const parsed = parse(preset.value, { pickerType: "single", timezone: effectiveTimezone });

      if (parsed.isValid && parsed.resolved) {
        // Check min/max date constraints using parser utility
        const resolvedDateStr = formatDateToYMD(parsed.resolved);
        const constraintResult = validateDateConstraints(resolvedDateStr, resolvedMinDate, resolvedMaxDate);

        if (!constraintResult.isValid) {
          // Don't apply preset if out of range
          return;
        }

        setErrorReason(null);

        // Preserve expressions unless immediatelyResolveExpressions is true
        if (parsed.isExpression && !immediatelyResolveExpressions) {
          onChange(parsed.normalized);
        } else {
          onChange(parsed.resolved.toISOString());
        }
        setIsOpen(false);
      }
    },
    [effectiveTimezone, onChange, resolvedMinDate, resolvedMaxDate, immediatelyResolveExpressions]
  );

  const handleTimezoneChange = React.useCallback(
    (newTimezone: string) => {
      // When "updateDisplay" is set, convert value so the same instant is shown
      if (timezoneSwitchBehavior === "updateDisplay") {
        const convertedValue = convertValueForTimezoneSwitch(value, effectiveTimezone, newTimezone);
        if (convertedValue !== value) {
          onChange(convertedValue);
        }
      }
      // "keepDisplay" (default): value stays the same, only interpretation changes

      onTimezoneChange?.(newTimezone);
    },
    [onTimezoneChange, timezoneSwitchBehavior, value, effectiveTimezone, onChange]
  );

  // Check if value contains a relative expression
  const hasExpression = React.useMemo(() => checkIsExpression(value), [value]);

  // Handler to convert expression to fixed ISO date
  const handleApplyExpression = React.useCallback(() => {
    applyExpression(value, effectiveTimezone, onChange);
  }, [value, effectiveTimezone, onChange]);

  // ---------------------------------------------------------------------------
  // Computed Values
  // ---------------------------------------------------------------------------

  const showPresets = presets && presets.length > 0;

  // Resolve expression for calendar highlighting (expressions like "now-7d" need to be resolved)
  const selectedDateStr = React.useMemo(() => {
    if (!value) return null;
    if (isAbsoluteDate(value)) {
      const date = new Date(value);
      // Guard against invalid dates
      if (Number.isNaN(date.getTime())) return null;
      return formatDateToYMD(date);
    }
    // Resolve expression for calendar display
    const parsed = parse(value, { pickerType: "single", timezone: effectiveTimezone });
    return parsed.resolved ? formatDateToYMD(parsed.resolved) : null;
  }, [value, effectiveTimezone]);

  // Tooltip showing error reason or resolved value in UTC and local time
  // For absolute dates, memoize. For expressions (like "now"), evaluate fresh each render.
  const absoluteDateTooltip = React.useMemo(() => {
    if (requiredError) return "This field is required";
    if (errorReason) return errorReason;
    if (!value) return "Try: 2024-01-15, now-7d, yesterday, last week";
    if (!isAbsoluteDate(value)) return null; // Will be computed below
    const date = new Date(value);
    // Guard against invalid dates
    if (Number.isNaN(date.getTime())) return "Invalid date";
    return formatTooltipWithTimezone(date, effectiveTimezone);
  }, [value, errorReason, requiredError, effectiveTimezone]);

  // Compute tooltip - use memoized value for absolute dates, fresh evaluation for expressions
  const inputTitle = absoluteDateTooltip !== null
    ? absoluteDateTooltip
    : (() => {
        // Expression - resolve fresh each render with forDisplay: true for proper timezone handling
        const date = resolveExpression(value ?? "", effectiveTimezone, { forDisplay: true });
        return date ? formatTooltipWithTimezone(date, effectiveTimezone) : undefined;
      })();

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Build class name with granularity variant
  const pickerClassName = [
    "date-picker",
    `date-picker--${granularity}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // Handler to force re-render on hover for dynamic tooltip evaluation
  const handleMouseEnter = React.useCallback(() => {
    // Only trigger re-render if value is an expression (not absolute date)
    if (value && !isAbsoluteDate(value)) {
      setHoverTick(t => t + 1);
    }
  }, [value]);

  return (
    <div
      ref={anchorRef}
      className={pickerClassName}
      style={style}
    >
      <div className="date-picker__input-wrapper" onMouseEnter={handleMouseEnter}>
        <DateInput
          value={inputText}
          onChange={handleInputChange}
          isError={!!errorReason || requiredError}
          previousValue={previousValue === undefined ? null : previousValue === null ? "" : isAbsoluteDate(previousValue) ? formatForDisplay(new Date(previousValue)) : previousValue}
          onUndo={handleUndo}
          allowClear={allowClear}
          onClear={handleClear}
          disabled={disabled}
          placeholder={effectivePlaceholder}
          onClick={handleInputClick}
          onFocus={handleInputFocus}
        />
        {!isOpen && <PickerTooltip content={inputTitle} className="date-picker__tooltip" />}
      </div>

      <Popover
        open={isOpen}
        onClose={handleClose}
        anchorEl={anchorRef.current}
      >
        <div className={`date-picker__popover${showPresets ? " date-picker__popover--with-presets" : ""}`}>
          {/* Presets */}
          {showPresets && (
            <div className="date-picker__presets">
              <PresetList
                presets={presets}
                activeValue={value ? inputText : undefined}
                onPresetClick={handlePresetClick}
              />
            </div>
          )}

          {/* Calendar with optional Time */}
          <div className="date-picker__calendar">
            <CalendarBox
              year={calendarYear}
              month={calendarMonth}
              onMonthChange={handleMonthChange}
              selectedDate={selectedDateStr}
              minDate={resolvedMinDate}
              maxDate={resolvedMaxDate}
              onDateClick={handleDateClick}
              timezone={effectiveTimezone}
              showTime={showTimeInput && (!value || isAbsoluteDate(value))}
              timeValue={timeValue}
              onTimeChange={handleTimeChange}
              onTimeBlur={handleTimeBlur}
              isTimeError={timeError}
              previousTimeValue={previousTimeValue}
              onTimeUndo={handleTimeUndo}
              timeGranularity={granularity === "date" ? "minute" : granularity}
              timeLabel="Time"
            />

            {/* Footer - Apply button above Timezone Selector */}
            {(hasExpression || onTimezoneChange) && (
              <div className="date-picker__footer">
                {hasExpression && (
                  <Button
                    type="primary"
                    size="small"
                    onClick={handleApplyExpression}
                    title="Convert relative expression to fixed date for shareable URL"
                  >
                    Apply expression
                  </Button>
                )}
                {onTimezoneChange && (
                  <TimezoneSelector
                    value={effectiveTimezone}
                    onChange={handleTimezoneChange}
                    localTimezone={LOCAL_TIMEZONE}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </Popover>
    </div>
  );
});
